// ABOUTME: Last add-inbound step from Inkoopadvies: preview of the purchase order mail to the supplier, editable before saving.
// ABOUTME: Shown when the supplier has an email address; the switch starts as the supplier's autoSendInbound and decides whether it is sent.

import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import ShSwitch from '../../../../theme/inputs/ShSwitch';
import Text from '../../../../theme/text/Text';
import SupplierMailEditor from '../../../EmailTemplates/SupplierMailEditor';
import { usePreviewInboundSupplierMailMutation } from '../../../../../newapi/inbound/inboundsSlice';

const Content = (props) => {
    const { t } = useTranslation();
    const data = props.data;
    const [previewMail, { isLoading, isError }] = usePreviewInboundSupplierMailMutation();
    const [mail, setMail] = useState(null);
    const [edited, setEdited] = useState(null);
    const [dirty, setDirty] = useState(false);
    // Starts as the supplier's own choice, which Details puts in sendToSupplier. Coming back to the
    // step keeps the choice.
    const [send, setSend] = useState(!!data.sendToSupplier || !!data.supplierMail);

    // Built fresh every time the step opens, so changed lines in an earlier step are in the mail.
    useEffect(() => {
        previewMail({
            relationId: data.relationId,
            supplierId: data.supplierId,
            reference: data.reference,
            referenceIsPoNumber: !!data.referenceIsPoNumber,
            shipmentDate: data.shipmentDate,
            relationRemark: data.relationRemark,
            inboundLines: data.inboundLines,
        }).unwrap().then((result) => {
            setMail(result);
            setEdited({ subject: result.subject, html: result.html });
        }).catch(() => setMail(null));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // An edited mail goes out as supplierMail; otherwise sendToSupplier sends the template mail,
    // also when saving before the preview loaded or after it failed. Off sends nothing, also to a supplier
    // that gets every inbound mailed. The reference the preview used lets the backend swap an expected
    // PO number for the one the inbound really gets.
    useEffect(() => {
        props.handleDataUpdate({
            supplierMail: send && dirty ? { ...edited, reference: mail?.reference } : null,
            sendToSupplier: send && !dirty,
        });
    }, [send, dirty, edited]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleEdit = (value) => {
        setEdited(value);
        setDirty(true);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 120 }}><Text>{t("send_inbound_to_supplier")}</Text></Box>
                <ShSwitch checked={send} onChange={(e) => setSend(e.target.checked)} />
            </Box>
            {!send && <Text light>{t("supplier_mail_later_hint")}</Text>}
            {isLoading && <Text light>{t("loading")}</Text>}
            {isError && <Text light>{t("supplier_mail_preview_failed")}</Text>}
            {/* Hidden rather than unmounted, so switching the mail off and on keeps the edits. */}
            {mail && edited &&
                <Box sx={{ display: send ? "block" : "none" }}>
                    <SupplierMailEditor mail={mail} subject={edited.subject} onChange={handleEdit} />
                </Box>
            }
        </Box>
    );
};

const MailPreview = (props) => {
    const { t } = useTranslation();
    return (
        <ProgressionTab
            tabId="tabSupplierMail"
            subtitle={t("purchaseorder_email")}
            step={4}
            maxSteps={4}
            allowSaveAndOpen={props.allowSaveAndOpen}
            // Room for the whole mail at its own width, without a second scrollbar around the preview.
            sx={{ body: { maxHeight: { md: "75vh" }, minWidth: { md: 720 } } }}
        >
            <Content />
        </ProgressionTab>
    );
};

export default MailPreview;
